import { Table, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { SlaExpandedDetailGrid } from '~/app/landing/sla/shared/components/SlaExpandedDetailGrid';
import {
  MAINTENANCE_SLA_ON_SITE_LEVELS,
  MAINTENANCE_SLA_REMOTE_LEVELS,
  type MaintenanceSlaInterventionMode,
  type MaintenanceSlaSavedLevel,
  type MaintenanceSlaSavedPricingCombination,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import { MaintenanceSlaLevelHelp } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaLevelHelp/MaintenanceSlaLevelHelp';
import { MaintenanceSlaTimersRead } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaTimersRead/MaintenanceSlaTimersRead';
import { minutesToDurationParts } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/rules';

function shouldShowOnSite(
  mode: MaintenanceSlaInterventionMode | null,
): boolean {
  return mode == null || mode === 'BOTH' || mode === 'ONSITE';
}

function shouldShowRemote(
  mode: MaintenanceSlaInterventionMode | null,
): boolean {
  return mode == null || mode === 'BOTH' || mode === 'REMOTE';
}

export interface MaintenanceSlaLevelGroupsReadProps {
  levels: MaintenanceSlaSavedLevel[];
  interventionMode: MaintenanceSlaInterventionMode | null;
  priceCurrency: string;
}

function formatAmount(
  amount: number | null,
  currency: string,
  dash: string,
): string {
  if (amount == null || Number.isNaN(amount)) {
    return dash;
  }

  return `${amount.toFixed(2)} ${currency || 'EUR'}`;
}

function formatDuration(minutes: number | null, dash: string): string {
  if (minutes == null) {
    return dash;
  }
  const parts = minutesToDurationParts(minutes);
  return `${parts.hours} h ${String(parts.minutes).padStart(2, '0')}`;
}

function LevelRead({
  level,
  priceCurrency,
}: {
  level: MaintenanceSlaSavedLevel;
  priceCurrency: string;
}) {
  const { t } = useTranslation('sla');
  const dash = t('oems.maintenance.dash');

  const pricingColumns = [
    {
      title: t('oems.maintenance.takeCharge'),
      dataIndex: 'takeChargeMinutes',
      render: (value: number | null) => formatDuration(value, dash),
    },
    {
      title: t('oems.maintenance.interventionDuration'),
      dataIndex: 'interventionMinutes',
      render: (value: number | null) => formatDuration(value, dash),
    },
    {
      title: t('oems.maintenance.resolutionDuration'),
      dataIndex: 'resolutionMinutes',
      render: (value: number | null) => formatDuration(value, dash),
    },
    {
      title: t('oems.maintenance.price'),
      dataIndex: 'priceAmount',
      render: (value: number | null) =>
        formatAmount(value, priceCurrency, dash),
    },
  ];

  return (
    <div style={{ marginTop: 12 }}>
      <Typography.Text strong>
        {level.code} <MaintenanceSlaLevelHelp code={level.code} />
      </Typography.Text>
      <SlaExpandedDetailGrid
        items={[
          {
            key: 'clause',
            label: t('oems.maintenance.penaltyClause'),
            value: level.penaltyClauseText ?? dash,
          },
          {
            key: 'penalty',
            label: t('oems.maintenance.penaltyAmount'),
            value: formatAmount(level.penaltyAmount, priceCurrency, dash),
          },
        ]}
      />
      <MaintenanceSlaTimersRead timers={level.timers} />
      {level.pricingCombinations.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          <Typography.Text strong>
            {t('oems.maintenance.pricingCombinations')}
          </Typography.Text>
          <Table<MaintenanceSlaSavedPricingCombination>
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={level.pricingCombinations}
            columns={pricingColumns}
            style={{ marginTop: 8 }}
          />
        </div>
      ) : null}
    </div>
  );
}

export function MaintenanceSlaLevelGroupsRead({
  levels,
  interventionMode,
  priceCurrency,
}: MaintenanceSlaLevelGroupsReadProps) {
  const { t } = useTranslation('sla');
  const onSite = levels.filter((level) =>
    (MAINTENANCE_SLA_ON_SITE_LEVELS as readonly string[]).includes(level.code),
  );
  const remote = levels.filter((level) =>
    (MAINTENANCE_SLA_REMOTE_LEVELS as readonly string[]).includes(level.code),
  );

  return (
    <div style={{ marginTop: 16 }}>
      {shouldShowOnSite(interventionMode) ? (
        <Typography.Text className="ds-section-header">
          <Tag color="blue" style={{ marginInlineEnd: 8 }}>
            N1 → N5
          </Tag>
          {t('oems.maintenance.sections.onSite')}
        </Typography.Text>
      ) : null}
      {shouldShowOnSite(interventionMode)
        ? onSite.map((level) => (
            <LevelRead
              key={level.code}
              level={level}
              priceCurrency={priceCurrency}
            />
          ))
        : null}
      {shouldShowRemote(interventionMode) ? (
        <Typography.Text className="ds-section-header">
          <Tag color="purple" style={{ marginInlineEnd: 8 }}>
            R1 → R5
          </Tag>
          {t('oems.maintenance.sections.remote')}
        </Typography.Text>
      ) : null}
      {shouldShowRemote(interventionMode)
        ? remote.map((level) => (
            <LevelRead
              key={level.code}
              level={level}
              priceCurrency={priceCurrency}
            />
          ))
        : null}
    </div>
  );
}
